import { LocalAudioTrack, Track } from 'livekit-client'
import * as Sentry from '@sentry/nuxt'
import { AggressiveNoiseProcessor } from './RnnoiseProcessor'
import type { CallSession } from './session'
import { MicGainChain } from './session'
import type { LiveKitState } from './types'
import { isNativeMicAvailable } from './nativeMic'
import { NativeSpeakerSink } from './nativeSpeaker'

/**
 * Everything audio-processing related that isn't connection lifecycle:
 * mic capture constraints, RNNoise/gain-chain sync, deafen, and the single
 * `AudioContext` the gain chain runs on. `LiveKitManager` owns the staleness
 * checks (`this.session === session`) around every call into this class —
 * it only knows how to manipulate a given, already-current session.
 */
export class MicAudioController {
  private audioContext: AudioContext | null = null
  // macOS: installed on the shared context as it's created — see `nativeSpeaker.ts`.
  private nativeSpeakerInstall: Promise<NativeSpeakerSink | null> | null = null
  private installedNativeSpeaker: NativeSpeakerSink | null = null

  microphoneCaptureOptions(state: Pick<LiveKitState, 'selectedMicId' | 'isEchoCancellationEnabled'>) {
    return {
      deviceId: state.selectedMicId,
      echoCancellation: state.isEchoCancellationEnabled,
      // The browser/OS-level noise suppression constraint used to be independently toggleable
      // (`isWebRTCNoiseSuppressionEnabled`), defaulted to `true`, and had no UI to ever turn it
      // off — so it silently ran underneath `AggressiveNoiseProcessor` (RNNoise) at all times,
      // which also defaults to `true` and *is* user-controllable via the "Шумоподавление"
      // button. Two noise suppressors stacked on every call regardless of what that button
      // showed. RNNoise is the single, user-facing noise-suppression control now; raw capture
      // stays unsuppressed so it isn't double-processed before RNNoise ever sees it.
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    }
  }

  normalizeVolume(value: number) {
    return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1
  }

  applyDeafState(session: CallSession, isDeafened: boolean) {
    for (const participant of session.room.remoteParticipants.values()) {
      for (const publication of participant.audioTrackPublications.values()) {
        publication.setEnabled(!isDeafened)
      }
    }
  }

  /** Installs/refreshes the RNNoise processor on the mic track to match `state.isNoiseSuppressionEnabled`. */
  async syncNoiseSuppression(session: CallSession, state: Pick<LiveKitState, 'isNoiseSuppressionEnabled' | 'micVolume'>) {
    const track = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
    if (!(track instanceof LocalAudioTrack)) return
    try {
      const processor = track.getProcessor()
      if (state.isNoiseSuppressionEnabled) {
        if (processor instanceof AggressiveNoiseProcessor) {
          processor.setVolume(state.micVolume)
        } else {
          const audioContext = await this.resumeAudioContext() ?? undefined
          await track.setProcessor(new AggressiveNoiseProcessor({ volume: state.micVolume, audioContext }))
          session.disposeMicGainChain()
        }
      } else if (processor) {
        await track.stopProcessor()
      }
    } catch (error) {
      console.error('[MicAudioController] Noise suppression update failed', error)
      Sentry.captureException(error, { extra: { action: 'noise-suppression' } })
    }
  }

  /**
   * Keeps the mic's effective output volume in sync with `micVolume`: RNNoise
   * has its own volume control, so when it's active this only forwards the
   * value to it; otherwise a Web Audio gain chain is created/updated/torn
   * down (torn down entirely at volume 1, since a no-op gain node is pure
   * overhead) and swapped onto the track's `RTCRtpSender`.
   */
  async syncMicVolume(session: CallSession, micVolume: number) {
    const track = session.room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track
    if (!(track instanceof LocalAudioTrack) || !track.sender) {
      session.disposeMicGainChain()
      return
    }

    const processor = track.getProcessor()
    if (processor instanceof AggressiveNoiseProcessor) {
      processor.setVolume(micVolume)
      session.disposeMicGainChain()
      // `track.isUpstreamPaused` is true while "Прослушать себя" (`LiveKitManager.setSelfMonitoring`)
      // has us intentionally not sending — `replaceTrack` here would silently resume actually
      // transmitting to other participants just from adjusting the mic-volume slider. The self-
      // monitor's own playback tap reads `track.mediaStreamTrack` directly (which already reflects
      // `processor.setVolume()` above), so it stays in sync without this.
      if (!track.isUpstreamPaused && processor.processedTrack && track.sender.track !== processor.processedTrack) {
        await track.sender.replaceTrack(processor.processedTrack)
      }
      return
    }

    const sourceTrack = track.mediaStreamTrack
    if (micVolume === 1) {
      if (!track.isUpstreamPaused && session.getMicGainChain()) await track.sender.replaceTrack(sourceTrack)
      session.disposeMicGainChain()
      return
    }

    let chain = session.getMicGainChain()
    if (!chain || chain.sourceTrackId !== sourceTrack.id || chain.outputTrack.readyState === 'ended') {
      const context = await this.resumeAudioContext()
      if (!context) return
      const sourceNode = context.createMediaStreamSource(new MediaStream([sourceTrack]))
      const gainNode = context.createGain()
      const destination = context.createMediaStreamDestination()
      const outputTrack = destination.stream.getAudioTracks()[0]
      if (!outputTrack) throw new Error('Не удалось создать gain-track микрофона')
      sourceNode.connect(gainNode)
      gainNode.connect(destination)
      chain = new MicGainChain(sourceTrack.id, sourceNode, gainNode, destination, outputTrack)
      session.setMicGainChain(chain)
    }

    chain.gainNode.gain.setTargetAtTime(micVolume, chain.gainNode.context.currentTime, 0.01)
    // See the `isUpstreamPaused` comment above — same reasoning applies here.
    if (!track.isUpstreamPaused && track.sender.track !== chain.outputTrack) {
      await track.sender.replaceTrack(chain.outputTrack)
    }
  }

  /** Primes/resumes the shared `AudioContext` — called right as a call starts, while still inside the user gesture that triggered `connect()`. */
  async prime() {
    await this.resumeAudioContext()
  }

  /** The same shared context `prime()` warms up, handed to `Room` (`webAudioMix: { audioContext }`) so it reuses ours instead of opening a second one of its own — see `createRoomOptions`. */
  async sharedAudioContext() {
    return this.resumeAudioContext()
  }

  private async resumeAudioContext() {
    if (typeof window === 'undefined') return null
    if (!this.audioContext || this.audioContext.state === 'closed') {
      const Constructor = window.AudioContext || (window as any).webkitAudioContext
      if (!Constructor) return null
      this.audioContext = new Constructor()
      this.nativeSpeakerInstall = isNativeMicAvailable()
        ? NativeSpeakerSink.install(this.audioContext).then((sink) => {
            this.installedNativeSpeaker = sink
            return sink
          }, (error) => {
            console.error('[MicAudioController] Native audio output unavailable', error)
            Sentry.captureException(error, { extra: { action: 'native-speaker-install' } })
            return null
          })
        : null
    }
    const context = this.audioContext
    // Before anyone can connect to `destination` — the sink replaces it.
    await this.nativeSpeakerInstall
    if (context.state === 'suspended') await context.resume()
    return context
  }

  /** macOS only (null elsewhere, or if it failed to install): plays call audio through Rust. */
  async nativeSpeaker() {
    await this.resumeAudioContext()
    return this.nativeSpeakerInstall ? await this.nativeSpeakerInstall : null
  }

  /** Synchronous: a stop must take effect before a `start()` queued right after it. */
  stopNativeSpeaker() {
    void this.installedNativeSpeaker?.stop()
  }

  async close() {
    this.stopNativeSpeaker()
    this.nativeSpeakerInstall = null
    this.installedNativeSpeaker = null
    if (this.audioContext && this.audioContext.state !== 'closed') await this.audioContext.close()
    this.audioContext = null
  }
}
